import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { API_BASE_URL } from '@/app/core/http/api-base-url.token';
import { ApiEnvelope, unwrapEnvelope } from '@/app/core/http/api-envelope.model';
import { AuthHttpClient } from '@/app/core/http/auth-http-client';
import {
  AggregatedReportingData,
  AggregatedReportingQuery,
} from '../models/aggregated-reporting.model';
import { AvailablePeriods } from '../models/beneficiary-registry.model';
import { CatalogPage, PlanOption, ReportingCatalogQuery } from '../models/catalog.model';
import { AggregatedReportingRepository } from './aggregated-reporting.repository';
import { toSearchParams } from './catalog-params';

const REPORTING_PATH = '/beneficiarios/reporteria';
const PERIODS_PATH = `${REPORTING_PATH}/periodos`;
const PLANS_PATH = `${REPORTING_PATH}/planes`;

@Injectable()
export class AggregatedReportingHttpRepository extends AggregatedReportingRepository {
  private readonly http = inject(AuthHttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  override getReport(query: AggregatedReportingQuery): Observable<AggregatedReportingData> {
    return this.http
      .get<ApiEnvelope<AggregatedReportingData>>(`${this.baseUrl}${REPORTING_PATH}`, {
        params: this.toParams(query),
      })
      .pipe(map((envelope) => unwrapEnvelope(envelope, REPORTING_PATH)));
  }

  override getAvailablePeriods(): Observable<AvailablePeriods> {
    return this.http
      .get<ApiEnvelope<AvailablePeriods>>(`${this.baseUrl}${PERIODS_PATH}`)
      .pipe(map((envelope) => unwrapEnvelope(envelope, PERIODS_PATH)));
  }

  override getPlans(query: ReportingCatalogQuery): Observable<CatalogPage<PlanOption>> {
    return this.http
      .get<ApiEnvelope<CatalogPage<PlanOption>>>(`${this.baseUrl}${PLANS_PATH}`, {
        params: { from: query.from, to: query.to, ...toSearchParams(query) },
      })
      .pipe(map((envelope) => unwrapEnvelope(envelope, PLANS_PATH)));
  }

  private toParams(query: AggregatedReportingQuery): Record<string, string> {
    const params: Record<string, string> = { from: query.from, to: query.to };
    if (query.planName) params['planName'] = query.planName;
    return params;
  }
}
